import React from 'react'

import CsvDownloadLink from 'components/CsvDownloadLink'
import { FileUpload } from 'components/FileUpload'
import Loading from 'components/Loading/Loading'
import { domains } from 'lib/config'

import {
  BROWSE,
  DOWNLOAD_RESULTS_LABEL,
  DOWNLOAD_TEMPLATE_LABEL,
  EXAMPLE_IMAGE_INSTRUCTION,
  LOADING_MESSAGE,
  RESULTS_FILENAME_SUFFIX,
  TEMPLATE_FILENAME,
  UPLOAD,
} from './constants'
import {
  ExampleImage,
  FieldDescription,
  FieldDescriptionItem,
  FieldDescriptionList,
  FieldName,
  Instructions,
  Required,
  ResultsSection,
  StyledAutoTable,
  TemplateLinkWrapper,
  UploadButton,
  UploadSection,
} from './styles'
import type { CsvHeader, TableColumn } from 'types'
import type { FormattedPacingResult } from './PacingContainer'

export const csvTemplateHeaders: CsvHeader[] = [
  { label: 'SiteId', key: 'siteId' },
  { label: 'ProgramAccessCode', key: 'programAccessCode' },
  { label: 'TrainingStartDate', key: 'trainingStartDate' },
  { label: 'ProgramStartDate', key: 'programStartDate' },
  { label: 'SchoolYearEnd', key: 'schoolYearEnd' },
]

export const resultsHeaders: CsvHeader[] = [
  { label: 'Site Id', key: 'siteId' },
  { label: 'Site Name', key: 'siteName' },
  { label: 'Node Friendly Name', key: 'nodeFriendlyName' },
  { label: 'Start Date', key: 'startDate' },
  { label: 'RowNumber', key: 'rowNumber' },
  { label: 'Error', key: 'error' },
]

const FIELD_DESCRIPTIONS = [
  {
    name: 'SiteId:',
    description: 'The SS Id of the Site you want to add pacing dates to',
  },
  {
    name: 'Program Access Code:',
    description: 'Only "highschool" is available at this time',
  },
  { name: 'Training Start Date:', description: 'The training start date' },
  {
    name: 'Program Start Date:',
    description:
      'The start date of the program (must be after training start date)',
  },
  { name: 'School Year End:', description: 'The end date of the school year' },
]

const columns: TableColumn<FormattedPacingResult>[] = [
  {
    title: 'Site ID',
    fieldValue: (row: FormattedPacingResult) => row.siteId ?? null,
    sortValue: 'siteId',
  },
  {
    title: 'Site Name',
    fieldValue: (row: FormattedPacingResult) => row.siteName ?? null,
    sort: true,
    sortValue: 'siteName',
  },
  {
    title: 'Node Friendly Name',
    fieldValue: (row: FormattedPacingResult) => row.nodeFriendlyName ?? null,
    sort: true,
    sortValue: 'nodeFriendlyName',
  },
  {
    title: 'Start Date',
    fieldValue: (row: FormattedPacingResult) => {
      if (!row.startDate) return null
      return new Date(row.startDate).toDateString()
    },
  },
  {
    title: 'Error',
    fieldValue: (row: FormattedPacingResult) => row.error ?? null,
  },
]

interface PacingProps {
  file?: File
  fileName: string
  formattedResults?: FormattedPacingResult[]
  handlePacingUpload: (file: File) => void
  isLoading: boolean
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void
}

const Pacing: React.FC<PacingProps> = ({
  file,
  fileName,
  formattedResults,
  handlePacingUpload,
  isLoading,
  onFileChange,
}) => {
  const handleUploadClick = () => {
    if (file) {
      handlePacingUpload(file)
    }
  }

  const resultsFilename = `${new Date().toISOString()}${RESULTS_FILENAME_SUFFIX}`

  return (
    <>
      <Loading isLoading={isLoading} message={LOADING_MESSAGE} />
      <h3>Tool Description</h3>
      <p>Create Pacing dates for multiple schools</p>
      <hr />

      <h4>Fields Description</h4>
      <Required>All fields are required</Required>
      <FieldDescriptionList>
        {FIELD_DESCRIPTIONS.map(field => (
          <FieldDescriptionItem key={field.name}>
            <FieldName>{field.name}</FieldName>
            <FieldDescription>{field.description}</FieldDescription>
          </FieldDescriptionItem>
        ))}
      </FieldDescriptionList>
      <hr />

      <TemplateLinkWrapper>
        <CsvDownloadLink
          autoClick={false}
          data={[{}]}
          filename={TEMPLATE_FILENAME}
          headers={csvTemplateHeaders}
        >
          {DOWNLOAD_TEMPLATE_LABEL}
        </CsvDownloadLink>
      </TemplateLinkWrapper>

      <Instructions>
        Upload your <code>.xlsx</code> or <code>.csv</code> in the format below.
      </Instructions>
      <FileUpload
        disabled={isLoading}
        fileName={fileName}
        key={fileName}
        onFileChange={onFileChange}
      >
        {BROWSE}
      </FileUpload>
      <UploadSection>
        <UploadButton disabled={!file || isLoading} onClick={handleUploadClick}>
          {UPLOAD}
        </UploadButton>
      </UploadSection>

      <ResultsSection>
        {formattedResults && formattedResults.length > 0 && (
          <>
            <TemplateLinkWrapper>
              <CsvDownloadLink
                autoClick={false}
                data={formattedResults}
                filename={resultsFilename}
                headers={resultsHeaders}
              >
                {DOWNLOAD_RESULTS_LABEL}
              </CsvDownloadLink>
            </TemplateLinkWrapper>
            <StyledAutoTable
              borderless
              columns={columns}
              headThemeLight
              rows={formattedResults}
            />
          </>
        )}
      </ResultsSection>

      <Instructions>{EXAMPLE_IMAGE_INSTRUCTION}</Instructions>
      <ExampleImage
        alt="Pacing spreadsheet example"
        src={`${domains.CDN_DOMAIN}/assets/img/pacing_preview.png`}
      />
    </>
  )
}

export default Pacing
